import { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';

const RunawayButton = ({ children, className }) => {
  const buttonRef = useRef(null);
  const containerRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isRunning, setIsRunning] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Calculate safe zones (avoid top area where question and Yes button are)
  const getSafePosition = useCallback((mouseX, mouseY) => {
    if (!containerRef.current || !buttonRef.current) return position;

    const container = containerRef.current.getBoundingClientRect();
    const button = buttonRef.current.getBoundingClientRect();
    const buttonWidth = button.width || 100;
    const buttonHeight = button.height || 40;

    // Safe area: bottom 40% of screen, with padding from edges
    const padding = 20;
    const topSafeZone = container.height * 0.58; // Start from 58% down
    const minX = padding;
    const maxX = Math.max(minX, container.width - buttonWidth - padding);
    const minY = topSafeZone;
    const maxY = Math.max(minY, container.height - buttonHeight - padding);

    // Calculate direction away from mouse
    const buttonCenterX = position.x + buttonWidth / 2;
    const buttonCenterY = position.y + buttonHeight / 2;
    const dx = buttonCenterX - mouseX;
    const dy = buttonCenterY - mouseY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Move in opposite direction of mouse
    let newX, newY;
    if (distance < 120) {
      // Mouse is close, run away!
      const runDistance = 120 + Math.random() * 80;
      const angle = Math.atan2(dy, dx);
      newX = position.x + Math.cos(angle) * runDistance;
      newY = position.y + Math.sin(angle) * runDistance;

      // Add some randomness to make it playful
      newX += (Math.random() - 0.5) * 60;
      newY += (Math.random() - 0.5) * 40;
    } else {
      // Random jump if not close
      newX = minX + Math.random() * (maxX - minX);
      newY = minY + Math.random() * (maxY - minY);
    }

    // Clamp to safe bounds
    newX = Math.max(minX, Math.min(maxX, newX));
    newY = Math.max(minY, Math.min(maxY, newY));

    return { x: newX, y: newY };
  }, [position]);

  const handleInteraction = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;

    setIsRunning(true);
    const newPos = getSafePosition(mouseX, mouseY);
    setPosition(newPos);

    setTimeout(() => setIsRunning(false), 300);
  }, [getSafePosition]);

  // Initialize position on mount and window resize
  useEffect(() => {
    const initPosition = () => {
      if (containerRef.current) {
        const container = containerRef.current.getBoundingClientRect();
        const topSafeZone = container.height * 0.58;
        const buttonWidth = 100;
        setPosition({
          x: (container.width - buttonWidth) / 2,
          y: topSafeZone + 30,
        });
        setInitialized(true);
      }
    };

    initPosition();
    window.addEventListener('resize', initPosition);
    return () => window.removeEventListener('resize', initPosition);
  }, []);

  return (
    <div ref={containerRef} className="runaway-container">
      <motion.button
        ref={buttonRef}
        className={`no-button runaway ${className || ''}`}
        style={{
          position: 'absolute',
          left: position.x,
          top: position.y,
          opacity: initialized ? 1 : 0,
        }}
        animate={{
          left: position.x,
          top: position.y,
          rotate: isRunning ? 8 : 0,
          scale: isRunning ? 1.1 : 1,
        }}
        transition={{
          left: { type: 'spring', stiffness: 400, damping: 25 },
          top: { type: 'spring', stiffness: 400, damping: 25 },
          rotate: { type: 'spring', stiffness: 300, damping: 10 },
          scale: { type: 'spring', stiffness: 300, damping: 10 },
        }}
        onMouseEnter={handleInteraction}
        onMouseMove={handleInteraction}
        onTouchStart={handleInteraction}
        onTouchMove={handleInteraction}
      >
        {children}
      </motion.button>
    </div>
  );
};

export default RunawayButton;
